import config from "../../config/config";

// Display names use "English (Native)" — English first so an admin
// alphabetical scan / type-to-search finds entries by the English name,
// with the native form shown alongside for end-user clarity. Pseudo
// Language is pinned at the end so the prod splice below can drop it
// cleanly. Serbian (sr) is now fully translated and stays in prod.
const appTranslations = [
    { code: "ar", displayName: "Arabic (العربية)" },
    { code: "bg", displayName: "Bulgarian (Български)" },
    { code: "hr", displayName: "Croatian (Hrvatski)" },
    { code: "cs", displayName: "Czech (Čeština)" },
    { code: "da", displayName: "Danish (Dansk)" },
    { code: "nl", displayName: "Dutch (Nederlands)" },
    { code: "en", displayName: "English" },
    { code: "et", displayName: "Estonian (Eesti)" },
    { code: "fi", displayName: "Finnish (Suomi)" },
    { code: "fr", displayName: "French (Français)" },
    { code: "de", displayName: "German (Deutsch)" },
    { code: "el", displayName: "Greek (Ελληνικά)" },
    { code: "he", displayName: "Hebrew (עברית)" },
    { code: "hi", displayName: "Hindi (हिन्दी)" },
    { code: "hu", displayName: "Hungarian (Magyar)" },
    { code: "id", displayName: "Indonesian (Bahasa Indonesia)" },
    { code: "it", displayName: "Italian (Italiano)" },
    { code: "ja", displayName: "Japanese (日本語)" },
    { code: "ko", displayName: "Korean (한국어)" },
    { code: "lv", displayName: "Latvian (Latviešu)" },
    { code: "lt", displayName: "Lithuanian (Lietuvių)" },
    { code: "nb", displayName: "Norwegian Bokmål (Norsk Bokmål)" },
    { code: "fa", displayName: "Persian (فارسی)" },
    { code: "pl", displayName: "Polish (Polski)" },
    { code: "pt", displayName: "Portuguese (Português)" },
    { code: "pt-BR", displayName: "Portuguese (Brazil) (Português (Brasil))" },
    { code: "ro", displayName: "Romanian (Română)" },
    { code: "ru", displayName: "Russian (Русский)" },
    { code: "sr", displayName: "Serbian (Српски)" },
    { code: "zh-CN", displayName: "Simplified Chinese (简体中文)" },
    { code: "sk", displayName: "Slovak (Slovenčina)" },
    { code: "sl", displayName: "Slovenian (Slovenščina)" },
    { code: "es", displayName: "Spanish (Español)" },
    { code: "sv", displayName: "Swedish (Svenska)" },
    { code: "th", displayName: "Thai (ไทย)" },
    { code: "zh-TW", displayName: "Traditional Chinese (繁體中文)" },
    { code: "tr", displayName: "Turkish (Türkçe)" },
    { code: "uk", displayName: "Ukrainian (Українська)" },
    { code: "ur", displayName: "Urdu (اردو)" },
    { code: "vi", displayName: "Vietnamese (Tiếng Việt)" },
    { code: "pseudoLanguage", displayName: "Pseudo language" }
];

// Pseudo Language is a translator-testing locale — keep it out of prod.
// (Serbian used to be hidden too, but is now fully translated.)
if (!['dev', 'stage'].includes(config.environment)) {
    appTranslations.splice(-1);
}

// Best-effort match of the browser locale (e.g. "de-DE", "zh-CN", "pt-BR")
// to one of our supported translation codes. Returns null if nothing fits.
export const detectBrowserLanguageCode = () => {
    if (typeof navigator === 'undefined') return null;
    const candidates = []
        .concat(navigator.languages || [])
        .concat(navigator.language ? [navigator.language] : []);
    const codes = new Set(appTranslations.map(t => t.code));
    for (const raw of candidates) {
        if (!raw) continue;
        // exact: zh-CN, pt-BR — Intl tags use "-" same as our codes
        if (codes.has(raw)) return raw;
        // case-insensitive match against full tag
        const exact = [...codes].find(c => c.toLowerCase() === raw.toLowerCase());
        if (exact) return exact;
        // base prefix: "de-DE" -> "de", "nb-NO" -> "nb"
        const base = raw.split('-')[0].toLowerCase();
        const prefix = [...codes].find(c => c.toLowerCase() === base);
        if (prefix) return prefix;
    }
    return null;
};

// Return options with the browser-detected language pinned to the top so
// it sits in its own "Browser" group (Autocomplete groupBy renders a
// divider). The remaining entries keep their alphabetical order. The
// returned objects carry a `__group` field consumers can pass to groupBy.
export const withBrowserLanguageOnTop = (options = appTranslations) => {
    const browserCode = detectBrowserLanguageCode();
    if (!browserCode) return options.map(o => ({ ...o, __group: '' }));
    const top = options.find(o => o.code === browserCode);
    if (!top) return options.map(o => ({ ...o, __group: '' }));
    const rest = options.filter(o => o.code !== browserCode);
    return [
        { ...top, __group: 'Browser language' },
        ...rest.map(o => ({ ...o, __group: 'All languages' })),
    ];
};

export default appTranslations;
